import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ChevronRight, Clock, FileText, User } from "lucide-react";
import { api, absUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import { ArticleBody, formatArticleDate } from "@/components/ArticleBody";
import CategoryRFQModal from "@/components/CategoryRFQModal";
import { categoryPath } from "@/lib/paths";
import { ArticleCard } from "@/pages/ResourcesPage";
import NotFoundPage from "@/pages/NotFoundPage";

export default function ArticlePage() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [state, setState] = useState("loading");
  const [rfqOpen, setRfqOpen] = useState(false);
  const [topListings, setTopListings] = useState([]);

  useEffect(() => {
    setState("loading"); setA(null);
    api.get(`/resources/${slug}`).then(({ data }) => { setA(data); setState("ok"); window.scrollTo(0, 0); }).catch((e) => setState(e.response?.status === 404 ? "missing" : "error"));
  }, [slug]);

  const cat = a?.category || null;
  const rfqCategory = cat ? (cat.parent ? { ...cat.parent } : cat) : null;
  useEffect(() => {
    if (!cat) { setTopListings([]); return; }
    const qp = cat.parent ? `parent_category_id=${cat.parent.id}&category_id=${cat.id}` : `parent_category_id=${cat.id}`;
    api.get(`/listings?${qp}&limit=4`).then(({ data }) => setTopListings(data.items || [])).catch(() => setTopListings([]));
  }, [cat]);

  if (state === "missing") return <NotFoundPage />;
  if (state === "error") return <div className="max-w-3xl mx-auto px-4 py-20 text-sm text-[#B35C4A]" data-testid="article-error">Couldn't load this article right now.</div>;
  if (!a) return <div className="max-w-3xl mx-auto px-4 py-20 text-sm text-[#7D8C85] animate-pulse">Loading article…</div>;

  const img = a.image_url ? absUrl(a.image_url) : "";
  const seoTitle = a.seo_title || a.title;
  const seoDesc = a.seo_description || a.excerpt || `${a.title} — a guide for hotel buyers from Accommodation Suppliers.`;

  return (
    <article className="pb-16" data-testid="article-page">
      <SEO title={seoTitle} description={seoDesc} path={`/resources/${a.slug}`} image={img && /^https?:\/\//.test(img) ? img : undefined} type="article" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 md:pt-12">
        <nav className="flex items-center gap-1.5 text-xs text-[#7D8C85]" data-testid="article-breadcrumb">
          <Link to="/resources" className="hover:text-[#264839]">Resources</Link>
          {cat && <><ChevronRight className="w-3 h-3" /><Link to={categoryPath(cat)} className="hover:text-[#264839]">{cat.name}</Link></>}
        </nav>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#11211A] tracking-tight mt-4" data-testid="article-title">{a.title}</h1>
        {a.excerpt && <p className="text-base md:text-lg text-[#4A5550] mt-4" data-testid="article-excerpt">{a.excerpt}</p>}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#7D8C85] mt-5" data-testid="article-meta">
          <span className="inline-flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> <span data-testid="article-author">{a.author_name}</span></span>
          <time dateTime={a.published_at || ""} data-testid="article-date">{formatArticleDate(a.published_at)}</time>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {a.reading_minutes} min read</span>
        </div>
      </div>

      {img && (
        <figure className="max-w-5xl mx-auto px-4 sm:px-6 mt-8">
          <img src={img} alt={a.image_alt || a.title} className="w-full aspect-[16/8] object-cover bg-[#F0EFEA]" data-testid="article-image" />
        </figure>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-10 text-base md:text-[17px]">
        <ArticleBody text={a.body} />
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-14">
        <div className="bg-[#264839] text-white p-7 md:p-9" data-testid="article-rfq-box">
          <div className="tracking-overline text-[#E3B23C] mb-2">Next step</div>
          <div className="font-serif text-2xl md:text-3xl">{rfqCategory ? `Get quotes from ${rfqCategory.name.toLowerCase()} suppliers` : "Get quotes from hotel suppliers"}</div>
          <p className="text-white/80 text-sm mt-2">One request reaches up to four top-ranked suppliers. Free for buyers, always.</p>
          <div className="mt-5 flex flex-wrap items-center gap-4">
            {rfqCategory
              ? <button type="button" onClick={() => setRfqOpen(true)} className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#264839] font-medium hover:bg-[#F0EFEA] transition-colors" data-testid="article-rfq-btn"><FileText className="w-4 h-4" /> Request quotes</button>
              : <Link to="/get-quotes" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#264839] font-medium hover:bg-[#F0EFEA] transition-colors" data-testid="article-rfq-btn"><FileText className="w-4 h-4" /> Request quotes</Link>}
          </div>
        </div>
        {cat && (
          <div className="mt-6 flex flex-wrap items-center gap-3 text-sm" data-testid="article-related-links">
            <span className="text-[#7D8C85]">Related:</span>
            <Link to={categoryPath(cat)} className="inline-flex items-center gap-1 text-[#264839] font-medium hover:underline" data-testid="article-related-category">{cat.name} suppliers <ArrowRight className="w-3.5 h-3.5" /></Link>
            {cat.parent && <Link to={categoryPath(cat.parent)} className="inline-flex items-center gap-1 text-[#264839] hover:underline" data-testid="article-related-parent">{cat.parent.name} <ArrowRight className="w-3.5 h-3.5" /></Link>}
          </div>
        )}
      </div>

      {a.more?.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
          <div className="flex items-end justify-between mb-5">
            <h2 className="font-serif text-2xl text-[#11211A]">More guides</h2>
            <Link to="/resources" className="text-sm text-[#264839] hover:underline" data-testid="article-more-link">All resources</Link>
          </div>
          <div className="grid gap-6 md:grid-cols-3">{a.more.map((m) => <ArticleCard key={m.id} a={m} />)}</div>
        </div>
      )}

      {rfqOpen && rfqCategory && <CategoryRFQModal open={rfqOpen} onClose={() => setRfqOpen(false)} category={rfqCategory} topListings={topListings} source="article" />}
    </article>
  );
}
